<script lang="ts">
	import { ZoomIn, ZoomOut, Maximize, Undo2, Redo2, Trash2 } from 'lucide-svelte';

	interface Props {
		onZoomIn: () => void;
		onZoomOut: () => void;
		onFit: () => void;
		onUndo: () => void;
		onRedo: () => void;
		onClear: () => void;
		canUndo?: boolean;
		canRedo?: boolean;
		canClear?: boolean;
	}

	let {
		onZoomIn,
		onZoomOut,
		onFit,
		onUndo,
		onRedo,
		onClear,
		canUndo = false,
		canRedo = false,
		canClear = false
	}: Props = $props();
</script>

<div class="controls-container">
	<button class="control-btn" onclick={onUndo} title="Undo" disabled={!canUndo}>
		<Undo2 size={16} />
	</button>
	<button class="control-btn" onclick={onRedo} title="Redo" disabled={!canRedo}>
		<Redo2 size={16} />
	</button>
	<button class="control-btn danger" onclick={onClear} title="Clear chart" disabled={!canClear}>
		<Trash2 size={16} />
	</button>
	<button class="control-btn" onclick={onZoomOut} title="Zoom out">
		<ZoomOut size={16} />
	</button>
	<button class="control-btn" onclick={onZoomIn} title="Zoom in">
		<ZoomIn size={16} />
	</button>
	<button class="control-btn primary" onclick={onFit} title="Fit to screen">
		<Maximize size={16} />
	</button>
</div>

<style>
	.controls-container {
		position: absolute;
		bottom: 88px;
		right: 12px;
		display: flex;
		align-items: center;
		gap: 3px;
		padding: 3px;
		background: var(--color-bg-elevated);
		backdrop-filter: blur(12px);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		z-index: 1000;
	}

	.control-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-muted);
		border-radius: 6px;
		color: var(--color-text-tertiary);
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.control-btn:hover {
		background: var(--color-bg-panel-hover);
		border-color: var(--color-border-hover);
		color: var(--color-text-secondary);
	}

	.control-btn:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}

	.control-btn:disabled:hover {
		background: var(--color-bg-glass);
		border-color: var(--color-border-muted);
		color: var(--color-text-tertiary);
	}

	.control-btn:active {
		transform: scale(0.95);
	}

	.control-btn.primary {
		background: var(--color-success-soft);
		border-color: var(--color-success);
		color: var(--color-success);
	}

	.control-btn.primary:hover {
		background: color-mix(in srgb, var(--color-success) 24%, transparent);
		border-color: var(--color-success);
		color: color-mix(in srgb, var(--color-success) 82%, white);
	}

	.control-btn.danger {
		background: var(--color-danger-soft);
		border-color: var(--color-danger);
		color: var(--color-danger);
	}

	.control-btn.danger:hover:not(:disabled) {
		background: color-mix(in srgb, var(--color-danger) 24%, transparent);
		border-color: var(--color-danger);
		color: color-mix(in srgb, var(--color-danger) 75%, white);
	}

	.control-btn svg {
		width: 14px;
		height: 14px;
	}

	.divider {
		width: 1px;
		height: 18px;
		background: var(--color-border-muted);
	}

	@media (max-width: 768px) {
		.controls-container {
			/* positioning handled by parent wrapper */
		}
	}
</style>
